import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Check, MapPin, Star, ArrowRight, Menu, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">lokalnie.pro</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/szukaj" className="text-sm font-medium text-muted-foreground hover:text-foreground">Katalog</Link>
            <Link to="/pricing" className="text-sm font-medium text-foreground">Cennik</Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">Panel</Link>
                {user.is_admin && <Link to="/admin" className="text-sm font-medium text-amber-600">Admin</Link>}
                <Button variant="ghost" onClick={logout}>Wyloguj</Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost">Zaloguj</Button></Link>
                <Link to="/rejestracja"><Button className="bg-amber-500 hover:bg-amber-600 text-white">Rozpocznij</Button></Link>
              </>
            )}
          </nav>
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </header>
  );
};

const PricingCard = ({ tier, name, price, features, isPopular, onSelect }) => {
  return (
    <Card className={`relative h-full ${isPopular ? 'border-amber-500 border-2 shadow-xl' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-amber-500 text-white px-4">Najpopularniejszy</Badge>
        </div>
      )}
      <CardHeader className="text-center pb-2">
        <CardTitle className="text-2xl">{name}</CardTitle>
        <CardDescription>
          <span className="text-4xl font-bold text-slate-900">{price}</span>
          <span className="text-slate-500"> zł/rok</span>
          <div className="text-xs text-slate-400 mt-1">Subskrypcja odnawiana automatycznie co rok. Anuluj w każdej chwili.</div>
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <ul className="space-y-3">
          {features.map(function(feature, idx) {
            return (
              <li key={idx} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-600">{feature}</span>
              </li>
            );
          })}
        </ul>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={() => onSelect(tier)}
          className={`w-full ${isPopular ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
          variant={isPopular ? 'default' : 'outline'}
          data-testid={`select-${tier}-btn`}
        >
          Wybierz pakiet
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  const businessId = searchParams.get('business_id');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pkgRes = await axios.get(`${API}/packages`);
        // Convert object to array
        const pkgArray = [
          { tier: 'starter', ...pkgRes.data.starter },
          { tier: 'business', ...pkgRes.data.business },
          { tier: 'premium', ...pkgRes.data.premium }
        ];
        setPackages(pkgArray);
        
        if (businessId) {
          const bizRes = await axios.get(`${API}/businesses/${businessId}`);
          setBusiness(bizRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [businessId]);

  const handleSelectPackage = async (tier) => {
    if (!user) {
      toast.error('Zaloguj się, aby kontynuować');
      navigate('/login');
      return;
    }

    if (!businessId) {
      toast.info('Wybierz firmę z katalogu');
      navigate('/szukaj');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout?business_id=${businessId}&package_tier=${tier}`,
        {},
        { 
          withCredentials: true,
          headers: { 'origin': window.location.origin }
        }
      );

      if (response.data.mock) {
        navigate(`/payment/mock?payment_id=${response.data.payment_id}&business_id=${businessId}`);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Błąd podczas tworzenia płatności');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-amber-100 text-amber-700">Cennik</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Wybierz pakiet dla swojej firmy
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Profesjonalna strona internetowa wygenerowana przez AI. 
            Jedna opłata roczna, bez ukrytych kosztów.
          </p>
        </div>

        {business && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 mb-12">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={business.photos && business.photos[0] ? business.photos[0] : 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200'} 
                    alt={business.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-slate-900">{business.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Badge variant="secondary">{business.category}</Badge>
                    <span>•</span>
                    <span>{business.city}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700">Wybrano do aktywacji</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map(function(pkg) {
            return (
              <PricingCard
                key={pkg.tier}
                tier={pkg.tier}
                name={pkg.name}
                price={pkg.price_pln}
                features={pkg.features || []}
                isPopular={pkg.tier === 'business'}
                onSelect={handleSelectPackage}
              />
            );
          })}
        </div>

        {checkoutLoading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="p-8">
              <div className="flex items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                <span>Przygotowywanie płatności...</span>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
